import { canOpenPortalJob } from '../../../utils/portalJobNavigation';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Table } from 'react-bootstrap';
import JobScheduleTableHeader from './JobScheduleTableHeader';
import JobScheduleRow from './JobScheduleRow';
import { formatDateToMMDD } from '../utils/tableUtils';
import { isOutsideCurrentYear, isWeekBoundaryDate } from '../utils/highlightingUtils';

const compactHighlightedRow = { id: null, week: null };

const CompactJobScheduleWeek = ({
  title,
  jobs,
  filters,
  weekColumns,
  pumpDetails,
  hasTbd,
  hasIncompleteTasks
}) => {
  const [expandedJobs, setExpandedJobs] = useState(() => new Set());
  const headerViewportRef = useRef(null);
  const rowViewportRefs = useRef(new Map());
  const isSynchronizingScroll = useRef(false);
  const compactFilters = useMemo(() => ({
    ...filters,
    hideCurrentPlanShipDate: true,
    compactInlineIdentity: true
  }), [filters]);

  const toggleJobDetails = useCallback((jobKey) => {
    setExpandedJobs((currentExpandedJobs) => {
      const nextExpandedJobs = new Set(currentExpandedJobs);

      if (nextExpandedJobs.has(jobKey)) {
        nextExpandedJobs.delete(jobKey);
      } else {
        nextExpandedJobs.add(jobKey);
      }

      return nextExpandedJobs;
    });
  }, []);

  const synchronizeScroll = useCallback((event) => {
    if (isSynchronizingScroll.current) return;

    const source = event.currentTarget;
    const scrollLeft = source.scrollLeft;
    isSynchronizingScroll.current = true;

    if (headerViewportRef.current && headerViewportRef.current !== source) {
      headerViewportRef.current.scrollLeft = scrollLeft;
    }

    rowViewportRefs.current.forEach((viewport) => {
      if (viewport !== source) {
        viewport.scrollLeft = scrollLeft;
      }
    });

    window.requestAnimationFrame(() => {
      isSynchronizingScroll.current = false;
    });
  }, []);

  const registerRowViewport = useCallback((jobKey, node) => {
    if (node) {
      rowViewportRefs.current.set(jobKey, node);
    } else {
      rowViewportRefs.current.delete(jobKey);
    }
  }, []);

  const handleReadOnlyInteraction = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const renderReadOnlyCell = useCallback((job, field, displayValue) => displayValue, []);
  const handleReadOnlyRowClick = useCallback(() => {}, []);

  return (
    <section className="schedule-compact-week">
      <h5 className="schedule-compact-week-heading">{title}</h5>

      {expandedJobs.size > 0 && (
        <div
          className="schedule-compact-shared-header-viewport"
          ref={headerViewportRef}
          onScroll={synchronizeScroll}
          role="region"
          aria-label={`${title} schedule columns`}
          tabIndex="0"
        >
          <Table bordered className={`job-schedule-table schedule-compact-metrics-table schedule-compact-header-table no-action-column ${filters.softwareView ? 'software-view' : ''} ${filters.controlPanelView ? 'control-panel-view' : ''} ${filters.northPrairieView ? 'north-prairie-view' : ''}`}>
            <JobScheduleTableHeader filters={compactFilters} showActionColumn={false} />
          </Table>
        </div>
      )}

      {jobs.map((job, index) => {
        const jobKey = `${job.ID}-${job.JobNumber}`;
        const canOpenJob = canOpenPortalJob(job);
        const isExpanded = expandedJobs.has(jobKey);
        const detailsId = `schedule-details-${title}-${job.ID || index}`.replace(/[^a-zA-Z0-9_-]/g, '-');

        return (
          <article className="schedule-compact-job" key={jobKey}>
            <div className="schedule-compact-summary">
              <div className="schedule-compact-identity-field schedule-compact-job-number">
                {canOpenJob ? (
                  <a href={`/${job.ID}/generalInfo`}>{job.JobNumber || "Unassigned"}</a>
                ) : (
                  <span>{job.JobNumber || "Unassigned"}</span>
                )}
              </div>
              <div className="schedule-compact-identity-field schedule-compact-job-name">
                <span>{job.ProjectName || "No project name"}</span>
              </div>
              <div className="schedule-compact-ship-date">
                <span className="schedule-compact-ship-date-label">Current Plan Ship</span>
                <span>
                  {formatDateToMMDD(job.CurrentPlanShipDate) || "Not scheduled"}
                  {isWeekBoundaryDate(job.CurrentPlanShipDate, weekColumns) === 'start' && (
                    <i className="bi bi-calendar-x ms-1 text-danger" title="Date is a Sunday" aria-label="Date is a Sunday" />
                  )}
                  {isWeekBoundaryDate(job.CurrentPlanShipDate, weekColumns) === 'end' && (
                    <i className="bi bi-calendar-x ms-1 text-danger" title="Date is a Saturday" aria-label="Date is a Saturday" />
                  )}
                  {isOutsideCurrentYear(job.CurrentPlanShipDate) && (
                    <i className="bi bi-calendar-event ms-1 text-warning" title="Date is outside the current year" aria-label="Date is outside the current year" />
                  )}
                </span>
              </div>
              <button
                type="button"
                className="schedule-compact-expand-button"
                onClick={() => toggleJobDetails(jobKey)}
                aria-expanded={isExpanded}
                aria-controls={detailsId}
                aria-label={`${isExpanded ? 'Hide' : 'Show'} schedule details for ${job.JobNumber || job.ProjectName || 'job'}`}
              >
                <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} aria-hidden="true" />
              </button>
            </div>

            <div className="schedule-compact-details" id={detailsId} hidden={!isExpanded}>
              <div
                className="schedule-compact-metrics-viewport"
                ref={(node) => registerRowViewport(jobKey, node)}
                onScroll={synchronizeScroll}
                role="region"
                aria-label={`Schedule details for ${job.JobNumber || job.ProjectName || 'job'}`}
                tabIndex="0"
              >
                <Table bordered className={`job-schedule-table schedule-compact-metrics-table schedule-compact-values-table no-action-column ${filters.softwareView ? 'software-view' : ''} ${filters.controlPanelView ? 'control-panel-view' : ''} ${filters.northPrairieView ? 'north-prairie-view' : ''}`}>
                  <tbody>
                    <JobScheduleRow
                      job={job}
                      index={index}
                      handleRowClick={handleReadOnlyRowClick}
                      highlightedRow={compactHighlightedRow}
                      handleOpenModal={handleReadOnlyInteraction}
                      handleCellDoubleClick={handleReadOnlyInteraction}
                      renderEditableCell={renderReadOnlyCell}
                      hasTbd={hasTbd}
                      hasIncompleteTasks={hasIncompleteTasks}
                      filters={compactFilters}
                      weekColumns={weekColumns}
                      showActionColumn={false}
                      pumpDetails={pumpDetails}
                    />
                  </tbody>
                </Table>
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
};

export default CompactJobScheduleWeek;
